/**
 * Collections — am I collecting what I billed?
 *
 * The one report a manager opens first, and it is built around a single comparison: what was
 * raised in a month against what arrived in it. The **gap** between the two is the report's real
 * subject, so it is drawn as its own series rather than left to be inferred from two bar heights
 * — a reader should not have to subtract.
 *
 * The gap is signed and is not clamped at zero. A month in which more arrived than was raised is
 * a real and ordinary event — an arrear from last year being cleared — and flattening it would
 * hide exactly the month somebody wants to see.
 */

import { useQuery } from "@tanstack/react-query";
import { monthLabel, paymentMethodLabel, type PaymentMethod } from "@rentorng/core";
import { api } from "@/lib/api";
import { useFormat } from "@/lib/format";
import { ColumnChart, Donut, SERIES, TrendLine } from "@/components/charts";
import { Card, CardTitle, EmptyState, Table } from "@/components/ui";
import { ChartCard, Figure, FigureRow, PeriodChoice, ReportHeader, ReportState, usePeriod, useCompactMoney } from "./shared";

interface Collections {
  series: Array<{ key: string; billed: number; collected: number; gap: number }>;
  byMethod: Array<{ method: string; amount: number }>;
  totalBilled: number;
  totalCollected: number;
  totalGap: number;
  rate: number;
  bestMonth: { key: string; collected: number } | null;
  worstMonth: { key: string; collected: number } | null;
  months: number;
}

export function CollectionsReportPage() {
  const fmt = useFormat();
  const compact = useCompactMoney();
  const [months, setMonths] = usePeriod("months", 12);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["report-collections", months],
    queryFn: () => api.get<Collections>(`/reports/collections?months=${months}`),
  });

  if (!data) {
    return (
      <div>
        <ReportHeader title="Collections" question="Am I collecting what I billed, and where is the gap?" />
        <ReportState isLoading={isLoading} error={error} retry={() => refetch()} />
      </div>
    );
  }

  const full = (value: number) => fmt.money(value);
  const points = data.series.map((point) => ({
    label: monthLabel(point.key).split(" ")[0],
    full: monthLabel(point.key),
    values: [point.billed, point.collected],
  }));

  /* The rate per month, from the same two series the columns are drawn from. Worked out here and
     nowhere else on the page, so the line and the columns cannot say different things. */
  const rates = data.series.map((point) => ({
    label: monthLabel(point.key).split(" ")[0],
    full: monthLabel(point.key),
    value: point.billed > 0 ? Math.round((point.collected / point.billed) * 100) : 0,
    detail: point.billed > 0 ? `${compact(point.collected)} of ${compact(point.billed)}` : "nothing billed",
  }));

  return (
    <div>
      <ReportHeader
        title="Collections"
        question="Am I collecting what I billed, and where is the gap?"
        subtitle={`${compact(data.totalCollected)} collected of ${compact(data.totalBilled)} billed`}
        actions={
          <PeriodChoice
            label="Over"
            value={months}
            onChange={setMonths}
            options={[
              { value: 6, label: "6m" },
              { value: 12, label: "12m" },
              { value: 24, label: "24m" },
            ]}
          />
        }
      />

      <div className="space-y-5">
        <FigureRow>
          <Figure label="Billed" value={compact(data.totalBilled)} sub={`Over ${data.months} months`} />
          <Figure label="Collected" value={compact(data.totalCollected)} tone="good" sub={`Over ${data.months} months`} />
          <Figure
            label="Gap"
            value={compact(data.totalGap)}
            tone={data.totalGap > 0 ? "bad" : "good"}
            sub={data.totalGap > 0 ? "Billed and not yet in" : "More arrived than was raised — older arrears clearing"}
          />
          <Figure
            label="Collection rate"
            value={`${data.rate}%`}
            tone={data.rate >= 85 ? "good" : data.rate >= 60 ? "warn" : "bad"}
            sub={
              data.bestMonth
                ? `Best month ${monthLabel(data.bestMonth.key)}, ${compact(data.bestMonth.collected)}`
                : undefined
            }
          />
        </FigureRow>

        <ChartCard
          title="Billed against collected"
          hint="The bars are the two figures side by side; the gap between a pair is what was raised and has not arrived. A month where the green bar is taller cleared an older debt."
        >
          <ColumnChart
            label={`Billed against collected, month by month, over ${data.months} months`}
            points={points}
            format={compact}
            highlight={1}
            series={[
              { label: "Billed", colour: "#c4cfcd", texture: "hatch" },
              { label: "Collected", colour: SERIES[0] },
            ]}
          />
        </ChartCard>

        <div className="grid gap-5 lg:grid-cols-2">
          <ChartCard
            title="Collection rate, month by month"
            hint="Collected as a share of what was billed in the same month. The axis starts at nought deliberately — a rate axis cropped to its own range turns ordinary drift into a cliff."
          >
            <TrendLine
              label={`Collection rate by month over ${data.months} months`}
              points={rates}
              ceiling={100}
              format={(value) => `${Math.round(value)}%`}
              valueColumn="Rate"
              colour={SERIES[1]}
              footnote="A month in which nothing was billed reads as nought per cent, because there was nothing to collect."
            />
          </ChartCard>

          <ChartCard
            title="How the money arrives"
            hint="Worth knowing before deciding whether wiring up online payment is worth the fee — if nine in ten payments are already transfers, it changes very little."
          >
            {data.byMethod.length === 0 ? (
              <EmptyState title="No payments in this period." />
            ) : (
              <Donut
                label={`Collections by payment method over ${data.months} months`}
                slices={data.byMethod.map((row) => ({
                  label: paymentMethodLabel(row.method as PaymentMethod),
                  value: row.amount,
                }))}
                format={full}
                centre={compact(data.totalCollected)}
                centreLabel="collected"
              />
            )}
          </ChartCard>
        </div>

        <Card padded={false}>
          <div className="p-5 sm:p-6">
            <CardTitle>Month by month</CardTitle>
          </div>
          <Table
            head={
              <tr>
                <th>Month</th>
                <th className="num">Billed</th>
                <th className="num">Collected</th>
                <th className="num">Gap</th>
                <th className="num">Rate</th>
              </tr>
            }
          >
            {data.series.map((point) => (
              <tr key={point.key}>
                <td>{monthLabel(point.key)}</td>
                <td className="num tabular">{full(point.billed)}</td>
                <td className="num tabular">{full(point.collected)}</td>
                <td className="num tabular">
                  {point.gap === 0 ? (
                    <span className="text-ink-300">—</span>
                  ) : (
                    <span className={point.gap > 0 ? "text-danger-700" : "text-brand-700"}>{full(point.gap)}</span>
                  )}
                </td>
                <td className="num tabular text-ink-500">
                  {point.billed > 0 ? `${Math.round((point.collected / point.billed) * 100)}%` : "—"}
                </td>
              </tr>
            ))}
            <tr className="font-medium">
              <td>Total</td>
              <td className="num tabular">{full(data.totalBilled)}</td>
              <td className="num tabular">{full(data.totalCollected)}</td>
              <td className="num tabular">{full(data.totalGap)}</td>
              <td className="num tabular">{data.rate}%</td>
            </tr>
          </Table>
        </Card>
      </div>
    </div>
  );
}
